import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { cities as staticCityList } from '../utils/cities.js';
import { API } from '../config';
import './CitySelect.css';

/**
 * Searchable dropdown to select a city. Fetches cities from API (system-wide list).
 * If user searches and no city matches, they can add the typed name as a new city (stored globally for all branches/businesses).
 */
const CitySelect = ({ value, onChange, required = false, placeholder = 'Select or search city', className = '', id, onError }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [cityList, setCityList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingCity, setAddingCity] = useState(false);
  const wrapperRef = useRef(null);

  const fetchCities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cities`);
      const list = res.data?.cities ?? [];
      setCityList(Array.isArray(list) ? list : []);
    } catch {
      setCityList([...staticCityList]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  // When editing, existing value might not be in list; include it in options
  const allOptions = [...cityList];
  if (value && value.trim() && !allOptions.some((c) => c.toLowerCase() === value.trim().toLowerCase())) {
    allOptions.unshift(value.trim());
  }

  const searchTrimmed = (search || '').trim();
  const searchLower = searchTrimmed.toLowerCase();
  const filteredOptions = searchLower
    ? allOptions.filter((city) => city.toLowerCase().includes(searchLower))
    : allOptions;

  const showAddNew = searchTrimmed.length > 0 && filteredOptions.length === 0;

  const displayValue = isOpen ? search : (value || '');

  const close = () => {
    setIsOpen(false);
    setSearch('');
    setHighlightIndex(0);
    if (searchTrimmed) {
      const exact = allOptions.find((c) => c.toLowerCase() === searchTrimmed.toLowerCase());
      if (exact) onChange(exact);
      else if (filteredOptions.length === 1) onChange(filteredOptions[0]);
    }
  };

  const selectCity = (city) => {
    onChange(city);
    setIsOpen(false);
    setSearch('');
    setHighlightIndex(0);
  };

  const handleAddNewCity = async () => {
    if (!searchTrimmed || addingCity) return;
    setAddingCity(true);
    try {
      await axios.post(`${API}/cities`, { name: searchTrimmed });
      await fetchCities();
      selectCity(searchTrimmed);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to add city';
      if (typeof onError === 'function') onError(msg);
      else console.error(msg);
    } finally {
      setAddingCity(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [search, value]);

  const optionCount = showAddNew ? 1 : filteredOptions.length;
  const effectiveHighlight = Math.min(highlightIndex, Math.max(0, optionCount - 1));

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        setSearch('');
        setHighlightIndex(0);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i < optionCount - 1 ? i + 1 : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i > 0 ? i - 1 : optionCount - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showAddNew && effectiveHighlight === 0) handleAddNewCity();
      else if (filteredOptions[effectiveHighlight]) selectCity(filteredOptions[effectiveHighlight]);
    }
  };

  return (
    <div ref={wrapperRef} className={`city-select-wrap ${className}`.trim()}>
      <input
        id={id}
        type="text"
        className="city-select-input"
        value={displayValue}
        onChange={(e) => {
          setSearch(e.target.value);
          if (!isOpen) setIsOpen(true);
          setHighlightIndex(0);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        aria-required={required}
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls="city-select-list"
        role="combobox"
        autoComplete="off"
      />
      <span className="city-select-chevron" aria-hidden>▼</span>
      {isOpen && (
        <ul
          id="city-select-list"
          className="city-select-dropdown"
          role="listbox"
        >
          {loading ? (
            <li className="city-select-item no-match">Loading cities…</li>
          ) : showAddNew ? (
            <li
              role="option"
              className={`city-select-item add-new ${effectiveHighlight === 0 ? 'highlight' : ''}`}
              onMouseEnter={() => setHighlightIndex(0)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleAddNewCity();
              }}
            >
              {addingCity ? 'Adding…' : `Add "${searchTrimmed}" as new city`}
            </li>
          ) : filteredOptions.length === 0 ? (
            <li className="city-select-item no-match">No matching city. Type a name and add it above.</li>
          ) : (
            filteredOptions.map((city, i) => (
              <li
                key={city}
                role="option"
                aria-selected={value === city}
                className={`city-select-item ${i === effectiveHighlight ? 'highlight' : ''}`}
                onMouseEnter={() => setHighlightIndex(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectCity(city);
                }}
              >
                {city}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default CitySelect;
